import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Send, CheckCircle2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import siteData from '@/data/siteData.json';

export const Contact = ({ selectedProduct, onClearProduct }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    product: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (selectedProduct) {
      setFormData((prev) => ({
        ...prev,
        product: selectedProduct.name,
        message: `Olá, gostaria de solicitar orçamento para o produto: ${selectedProduct.name} (${selectedProduct.brand}).\n\nDetalhes do produto:\n- Categoria: ${selectedProduct.category}\n- Marca: ${selectedProduct.brand}\n\nPor favor, entrem em contato com mais informações sobre preço e prazo de entrega.`,
      }));
      // Scroll to contact section
      const element = document.querySelector('#contato');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [selectedProduct]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (value) => {
    setFormData((prev) => ({ ...prev, product: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Build mailto link
    const subject = encodeURIComponent(
      `Solicitação de Orçamento - ${formData.product || 'Produtos em Geral'}`
    );
    const body = encodeURIComponent(
      `Nome: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Empresa: ${formData.company || 'Não informado'}\n` +
        `Telefone: ${formData.phone || 'Não informado'}\n` +
        `Produto de Interesse: ${formData.product || 'Não especificado'}\n\n` +
        `Mensagem:\n${formData.message}`
    );

    const mailtoLink = `mailto:${siteData.company.email}?subject=${subject}&body=${body}`;

    // Simulate a brief loading state
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Open email client
      window.location.href = mailtoLink;
      
      toast.success('Redirecionando para seu cliente de email...', {
        description: 'Complete o envio no seu aplicativo de email.',
      });

      // Reset form after delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          product: '',
          message: '',
        });
        setIsSubmitted(false);
        if (onClearProduct) onClearProduct();
      }, 3000);
    }, 500);
  };

  return (
    <section id="contato" className="py-20 md:py-28 bg-primary text-primary-foreground">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Info */}
          <div className="flex flex-col justify-center">
            <span className="inline-flex w-fit items-center gap-2 px-4 py-1.5 rounded-full bg-primary-foreground/10 text-primary-foreground/90 text-sm font-medium mb-6">
              <Mail className="h-4 w-4" />
              Contato
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Solicite seu Orçamento
            </h2>
            <p className="text-primary-foreground/70 text-base md:text-lg mb-8 max-w-lg">
              Preencha o formulário ao lado ou entre em contato diretamente pelo
              email. Nossa equipe responderá em até 24 horas úteis.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary-foreground/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="font-medium text-primary-foreground mb-1">
                    Email
                  </h4>
                  <a
                    href={`mailto:${siteData.company.email}`}
                    className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  >
                    {siteData.company.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary-foreground/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="font-medium text-primary-foreground mb-1">
                    Website
                  </h4>
                  <span className="text-primary-foreground/70">
                    {siteData.company.domain}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div>
            <Card className="bg-background text-foreground border-0 shadow-elegant">
              <CardHeader className="pb-4">
                <CardTitle className="font-display text-xl">
                  Formulário de Orçamento
                </CardTitle>
                <CardDescription>
                  Preencha os campos abaixo para solicitar seu orçamento
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isSubmitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
                      <CheckCircle2 className="h-8 w-8 text-success" />
                    </div>
                    <h3 className="font-display text-xl font-semibold mb-2">
                      Redirecionando...
                    </h3>
                    <p className="text-muted-foreground">
                      Complete o envio no seu cliente de email.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Name & Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-foreground">
                          Nome *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Seu nome"
                          required
                          className="bg-secondary border-border"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-foreground">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="seu@email.com"
                          required
                          className="bg-secondary border-border"
                        />
                      </div>
                    </div>

                    {/* Company & Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-foreground">
                          Empresa
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Nome da empresa"
                          className="bg-secondary border-border"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-foreground">
                          Telefone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(00) 00000-0000"
                          className="bg-secondary border-border"
                        />
                      </div>
                    </div>

                    {/* Product Select */}
                    <div className="space-y-2">
                      <Label htmlFor="product" className="text-foreground">
                        Produto de Interesse
                      </Label>
                      <Select
                        value={formData.product}
                        onValueChange={handleProductChange}
                      >
                        <SelectTrigger className="bg-secondary border-border">
                          <SelectValue placeholder="Selecione um produto" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="geral">
                            Consulta Geral / Múltiplos Produtos
                          </SelectItem>
                          {siteData.products.map((product) => (
                            <SelectItem key={product.id} value={product.name}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-foreground">
                        Mensagem *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Descreva sua necessidade, quantidade, ou outras informações relevantes..."
                        rows={5}
                        required
                        className="bg-secondary border-border resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Enviar Solicitação
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
